// CalibrationDashboard — workspace-level view of prioritization accuracy over time.
// Shows all delivered items, predicted vs actual scores, and accuracy trends.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, CheckCircle2, Clock, XCircle, ArrowRight, BarChart3 } from 'lucide-react';
import { getAllDeliveriesForWorkspace, computeCalibrationStats } from '@/lib/itemDeliveryService';
import { loadValueOutcomes, summarizeValueOutcomes, type AttributeOutcomeSummary, type OriginSummary } from '@/lib/valueOutcomesService';
import { DataTable, type Column } from '@/components/ds';

function valueAccuracyCell(s: OriginSummary): string {
  if (s.count === 0) return '—';
  const off = s.meanError == null ? '?' : s.meanError.toFixed(2);
  const exact = s.exactRate == null ? '?' : `${Math.round(s.exactRate * 100)}%`;
  return `${s.count} · off by ${off} · exact ${exact}`;
}

const VALUE_ACCURACY_COLUMNS: Column<AttributeOutcomeSummary>[] = [
  { key: 'attribute', header: 'Attribute', render: (a) => <span className="font-medium text-[var(--text-pri)]">{a.attributeKey}</span> },
  { key: 'all', header: 'Everyone', render: (a) => valueAccuracyCell(a.all) },
  { key: 'agent', header: 'Value Estimator', render: (a) => valueAccuracyCell(a.agent) },
  { key: 'person', header: 'People', render: (a) => valueAccuracyCell(a.person) },
];
import type { ItemDelivery, CalibrationStats } from '@/types/delivery';
import { DELIVERY_TYPE_LABELS } from '@/types/delivery';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';

const TYPE_ICON = {
  shipped: CheckCircle2,
  partial: Clock,
  cancelled: XCircle,
} as const;

const TYPE_COLOR = {
  shipped: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  partial: 'text-[var(--mustard)]',
  cancelled: 'text-[var(--coral)]',
} as const;

function AccuracyBar({ value }: { value: number }) {
  const pct = Math.min(100, Math.round(value * 100));
  const color = pct >= 90 ? 'bg-[var(--green)]' : pct >= 70 ? 'bg-[var(--mustard)]' : 'bg-[var(--coral)]';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-[var(--text-sec)] w-8 text-right">{pct}%</span>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color }: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${color}`} />
        <span className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-semibold text-[var(--text-pri)]">{value}</p>
      {sub && <p className="text-xs text-[var(--text-ter)] mt-0.5">{sub}</p>}
    </div>
  );
}

export function CalibrationDashboard() {
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const getItemById = useItemsStore((s) => s.getItemById);

  const [deliveries, setDeliveries] = useState<ItemDelivery[]>([]);
  const [stats, setStats] = useState<CalibrationStats | null>(null);
  const [valueAccuracy, setValueAccuracy] = useState<AttributeOutcomeSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!currentWorkspaceId) return;
    let cancelled = false;
    setLoading(true);
    void loadValueOutcomes({ workspaceId: currentWorkspaceId }).then((rows) => {
      if (!cancelled) setValueAccuracy(summarizeValueOutcomes(rows));
    });
    getAllDeliveriesForWorkspace(currentWorkspaceId)
      .then((data) => {
        if (cancelled) return;
        setDeliveries(data);
        setStats(computeCalibrationStats(data));
      })
      .catch((e) => { if (!cancelled) setErr(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentWorkspaceId]);

  const accuracyLabel = stats?.averageAccuracy != null
    ? `${Math.round(stats.averageAccuracy * 100)}%`
    : '—';

  const deliveryRateLabel = stats
    ? `${Math.round(stats.deliveryRate * 100)}%`
    : '—';

  return (
    <div className="px-6 py-8 max-w-5xl mx-auto space-y-8">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="h-5 w-5 text-[var(--text-ter)]" />
          <h1 className="text-xl font-semibold text-[var(--text-pri)]">Calibration</h1>
        </div>
        <p className="text-sm text-[var(--text-ter)]">
          How accurate were your prioritization scores? Compare predicted vs. actual outcomes to improve future estimates.
        </p>
      </div>

      {loading && (
        <p className="text-sm text-[var(--text-ter)] animate-pulse">Loading delivery records…</p>
      )}

      {err && (
        <div className="p-4 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">{err}</div>
      )}

      {!loading && stats && (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Total deliveries"
              value={stats.totalDeliveries}
              icon={BarChart3}
              color="text-[var(--text-ter)]"
            />
            <StatCard
              label="Delivery rate"
              value={deliveryRateLabel}
              sub={`${stats.shippedCount} shipped · ${stats.partialCount} partial`}
              icon={TrendingUp}
              color="text-[var(--green)] dark:text-[var(--text-ter)]"
            />
            <StatCard
              label="Cancelled"
              value={stats.cancelledCount}
              sub="dropped or deprioritized"
              icon={XCircle}
              color="text-[var(--coral)]"
            />
            <StatCard
              label="Score accuracy"
              value={accuracyLabel}
              sub="predicted vs actual (where captured)"
              icon={stats.averageAccuracy != null && stats.averageAccuracy >= 0.8 ? TrendingUp : stats.averageAccuracy != null && stats.averageAccuracy < 0.6 ? TrendingDown : Minus}
              color={stats.averageAccuracy != null && stats.averageAccuracy >= 0.8 ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : stats.averageAccuracy != null && stats.averageAccuracy < 0.6 ? 'text-[var(--coral)]' : 'text-[var(--text-ter)]'}
            />
          </div>

          {/* Values: accepted against actual (migration 823) */}
          {valueAccuracy.length > 0 && (
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
              <h2 className="text-sm font-semibold text-[var(--text-sec)] mb-1">Values: accepted against actual</h2>
              <p className="text-xs text-[var(--text-ter)] mb-4">Distance on the scale between what was accepted and what the work actually took. The Value Estimator beside the people it helps.</p>
              <DataTable
                columns={VALUE_ACCURACY_COLUMNS}
                rows={valueAccuracy}
                getRowKey={(a) => a.attributeKey}
                density="compact"
                caption="Accepted values against what the work actually took, per attribute"
                captionHidden
              />
            </div>
          )}

          {/* Framework breakdown */}
          {stats.frameworkBreakdown.length > 0 && (
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
              <h2 className="text-sm font-semibold text-[var(--text-sec)] mb-4">Accuracy by framework</h2>
              <div className="space-y-3">
                {stats.frameworkBreakdown.map((f) => (
                  <div key={f.toolName}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-[var(--text-sec)]">
                        {f.toolName.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                      <span className="text-xs text-[var(--text-ter)]">{f.count} items</span>
                    </div>
                    {f.averageAccuracy != null
                      ? <AccuracyBar value={f.averageAccuracy} />
                      : <p className="text-xs text-[var(--text-ter)] italic">No score data captured yet</p>
                    }
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Delivery log */}
          {deliveries.length === 0 ? (
            <div className="border border-dashed border-[var(--rule)] p-12 text-center">
              <CheckCircle2 className="h-10 w-10 text-[var(--text-ter)] mx-auto mb-3" />
              <p className="text-sm font-medium text-[var(--text-ter)] mb-1">No delivery records yet</p>
              <p className="text-xs text-[var(--text-ter)] max-w-sm mx-auto">
                Open any prioritized item and use "Mark as delivered" to start capturing outcomes.
                The more data you collect, the more useful your calibration becomes.
              </p>
              <Link
                to="/prioritization/items"
                className="inline-flex items-center gap-1.5 mt-4 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)] transition-colors"
              >
                Go to items <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          ) : (
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
              <div className="px-5 py-4 border-b border-[var(--rule)]">
                <h2 className="text-sm font-semibold text-[var(--text-sec)]">Delivery log</h2>
              </div>
              <div className="divide-y divide-[var(--rule)]">
                {deliveries.map((d) => {
                  const item = getItemById(d.itemId);
                  const Icon = TYPE_ICON[d.deliveryType];
                  return (
                    <div key={d.id} className="px-5 py-4 flex items-start gap-4 hover:bg-[var(--surface)] transition-colors">
                      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${TYPE_COLOR[d.deliveryType]}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          {item ? (
                            <Link
                              to={`/prioritization/items/${d.itemId}?tab=delivery`}
                              className="text-sm font-medium text-[var(--text-pri)] hover:text-[var(--text-ter)] truncate"
                            >
                              {item.title}
                            </Link>
                          ) : (
                            <span className="text-sm font-medium text-[var(--text-ter)] truncate font-mono text-xs">
                              {d.itemId}
                            </span>
                          )}
                          <span className="text-xs text-[var(--text-ter)] flex-shrink-0">
                            {DELIVERY_TYPE_LABELS[d.deliveryType]}
                          </span>
                        </div>
                        {d.notes && (
                          <p className="text-xs text-[var(--text-ter)] truncate">{d.notes}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        {d.scoreAccuracy != null && (
                          <p className={`text-xs font-medium mb-0.5 ${
                            d.scoreAccuracy >= 0.9 ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
                            : d.scoreAccuracy >= 0.7 ? 'text-[var(--mustard)]'
                            : 'text-[var(--coral)]'
                          }`}>
                            {Math.round(d.scoreAccuracy * 100)}% accuracy
                          </p>
                        )}
                        <p className="text-xs text-[var(--text-ter)]">
                          {new Date(d.deliveredAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
